/**
 * Theme switching (light / dark / system), the click-time counterpart of theme-bootstrap.js:
 * same storage key, same three effects (stored preference, data-theme, color-scheme), plus
 * keeping meta[name=theme-color] on the active --app-canvas.
 */
export type ThemePref = 'light' | 'dark' | 'system';

const KEY = 'app.ui.theme';
const media = matchMedia('(prefers-color-scheme: dark)');

export function themePref(): ThemePref {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Storage unavailable (private mode, blocked site data): fall back to the OS setting.
  }
  return 'system';
}

function apply(pref: ThemePref): void {
  const root = document.documentElement;
  const resolved = pref === 'system' ? (media.matches ? 'dark' : 'light') : pref;
  // "System" leaves data-theme off so the prefers-color-scheme rules govern.
  if (pref === 'system') delete root.dataset.theme;
  else root.dataset.theme = pref;
  root.style.colorScheme = resolved;
  const canvas = getComputedStyle(root).getPropertyValue('--app-canvas').trim();
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = canvas || (resolved === 'dark' ? '#100f13' : '#f6f6f6');
}

export function setTheme(pref: ThemePref): void {
  try {
    localStorage.setItem(KEY, pref);
  } catch {
    // Not persisted; the choice still applies to this page.
  }
  apply(pref);
}

/** Follow OS theme changes while the preference is "system". */
export function watchSystemTheme(onChange: () => void): void {
  media.addEventListener('change', () => {
    if (themePref() !== 'system') return;
    apply('system');
    onChange();
  });
}
